import {
  WebCodecsBrowserPlatform,
  WebCodecsWorkerRuntime,
  type WcWorkerInput,
  type WcWorkerOutput,
  type WcWorkerPlatform,
  type WcWorkerPort,
} from '@playcastdotio/PeerCore';

/**
 * Browser-local WebCodecs runtime for WebKit clients whose dedicated-worker rendering surface is
 * unavailable. It speaks the same worker-port contract and never creates an RTP video track.
 */
export class MainThreadWebCodecsPort implements WcWorkerPort {
  private readonly listeners = new Set<(message: WcWorkerOutput) => void>();
  private readonly runtime: WebCodecsWorkerRuntime;
  private terminated = false;

  constructor(platform: WcWorkerPlatform = MainThreadWebCodecsPort.browserPlatform()) {
    this.runtime = new WebCodecsWorkerRuntime(platform, message => {
      if (!this.terminated) for (const listener of this.listeners) listener(message);
    });
  }

  /** Start the runtime against the page canvas without transferring it to a worker. */
  startWithCanvas(session: string, canvas: HTMLCanvasElement): void {
    if (this.terminated) throw new Error('WebCodecs runtime is closed');
    this.runtime.receive({ type: 'start', session, canvas: canvas as unknown as OffscreenCanvas });
  }

  postMessage(message: WcWorkerInput): void {
    if (!this.terminated) this.runtime.receive(message);
  }

  onMessage(listener: (message: WcWorkerOutput) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  terminate(): void {
    if (this.terminated) return;
    this.runtime.receive({ type: 'close' });
    this.terminated = true;
    this.listeners.clear();
  }

  private static browserPlatform(): WcWorkerPlatform {
    const nowUs = () => Math.round(performance.now() * 1000);
    return new WebCodecsBrowserPlatform(nowUs, window);
  }
}
